// What the interface itself remembers: which screen and sheet are showing, big mode, the dancer's On1/On2 setting, the toast and the
// polite live region. The engine's state is separate (engine-state.ts).

import { engine } from '../engine/engine.js';
import { createStore } from './store.js';

export type SheetName =
  | 'menu' | 'loop' | 'speed' | 'cues' | 'testme' | 'calibrate' | 'truth' | 'diagnostics' | 'about' | 'help' | 'song' | 'review' | 'howto';

export interface Toast {
  readonly id: number;
  readonly message: string;
  /** Show an Undo button. */
  readonly undo: boolean;
  /** How many events the log held when this was shown: Undo is only offered while this change is still the last one. */
  readonly events: number;
}

/** 1: the phrase bar emphasises 1 and 5. 2: it emphasises 2 and 6. */
export type OnBeat = 1 | 2;

export interface UiState {
  readonly screen: 'home' | 'play';
  readonly sheet: SheetName | null;
  /** The song whose menu is open on Home. */
  readonly songId: string | null;
  readonly big: boolean;
  readonly on: OnBeat;
  /** Keep a copy of each opened song on this device (default on). */
  readonly keep: boolean;
  /** "Find the count automatically" (default on, S9e): a count is proposed when a song with no taps is opened. Off: tap first, as before. */
  readonly find: boolean;
  /** An updated copy of the app is installed and waiting: show "New version ready · Reload". */
  readonly update: boolean;
  readonly toast: Toast | null;
  readonly announcement: string;
  /** "Count set from 2 taps · Replay": shown in the status line once per song, for a few seconds (DESIGN §9). */
  readonly countNote: { readonly track: string; readonly taps: number } | null;
  /** "Tap removed · Put back": shown in the taps bar for a few seconds after a tap is removed. */
  readonly removedNote: number | null;
  /** The Review sheet started a replay: open it again when the replay ends. */
  readonly reopenReview: boolean;
}

const ON_KEY = 'salsacount.on';
const KEEP_KEY = 'salsacount.keep';
const FIND_KEY = 'salsacount.auto';

function loadOn(): OnBeat {
  try {
    return localStorage.getItem(ON_KEY) === '2' ? 2 : 1;
  } catch {
    return 1; // storage refused: the default, for this visit
  }
}

/** Keep a copy of each opened song on this device: on unless the dancer switched it off. */
function loadKeep(): boolean {
  try {
    return localStorage.getItem(KEEP_KEY) !== '0';
  } catch {
    return true;
  }
}

/** Find the count automatically: on unless the dancer switched it off. */
function loadFind(): boolean {
  try {
    return localStorage.getItem(FIND_KEY) !== '0';
  } catch {
    return true;
  }
}

export const ui = createStore<UiState>({ screen: 'home', sheet: null, songId: null, big: false, on: loadOn(), keep: loadKeep(), find: loadFind(), update: false, toast: null, announcement: '', countNote: null, removedNote: null, reopenReview: false });

export function setOnBeat(on: OnBeat): void {
  ui.patch({ on });
  try {
    localStorage.setItem(ON_KEY, String(on));
  } catch {
    /* kept for this visit only */
  }
}

export function setKeep(on: boolean): void {
  ui.patch({ keep: on });
  try {
    localStorage.setItem(KEEP_KEY, on ? '1' : '0');
  } catch {
    /* kept for this visit only */
  }
}

/** The setting "Find the count automatically": remembered on this device and told to the engine now and at start. */
export function setFind(on: boolean): void {
  ui.patch({ find: on });
  engine.setFindAuto(on);
  try {
    localStorage.setItem(FIND_KEY, on ? '1' : '0');
  } catch {
    /* kept for this visit only */
  }
}
engine.setFindAuto(ui.get().find);

export function openSheet(sheet: SheetName): void {
  ui.patch({ sheet });
}

export function closeSheet(): void {
  ui.patch({ sheet: null, songId: null });
}

export function toggleBig(): void {
  ui.patch({ big: !ui.get().big });
  announce(ui.get().big ? 'Big mode on' : 'Big mode off');
}

// ── what the device has ──

/** Is there a keyboard to press T on? A mouse or a trackpad (hover and a fine pointer) usually means a computer; a phone's touch screen means not. */
export function hasKeyboard(): boolean {
  try {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  } catch {
    return false;
  }
}

/** Big mode, or landscape on a phone (which shows only the numeral and the phrase bar without a button): nothing else is said there (DESIGN §10, rule 1). */
export function inBigMode(): boolean {
  if (ui.get().big) return true;
  try {
    return window.matchMedia('(orientation: landscape) and (max-height: 520px)').matches;
  } catch {
    return false;
  }
}

// ── the live region ──

let flip = false;

/** Say something to screen readers, politely. The same words twice in a row are still heard. */
export function announce(message: string): void {
  flip = !flip;
  ui.patch({ announcement: flip ? message : `${message} ` });
}

// ── the toast ──

let toastId = 0;
let toastTimer = 0;

/** A short message, with Undo after a correction. It goes after eight seconds. */
export function showToast(message: string, undo = false): void {
  const id = ++toastId;
  ui.patch({ toast: { id, message, undo, events: engine.getState().events.length } });
  announce(undo ? `${message}. Undo is available.` : message);
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    if (ui.get().toast?.id === id) ui.patch({ toast: null });
  }, 8000);
}

export function dismissToast(): void {
  window.clearTimeout(toastTimer);
  ui.patch({ toast: null });
}

// ── screens ──

/** Go to the player (or the lab). The browser's Back button returns to Home. */
export function enterPlayer(): void {
  if (ui.get().screen === 'play') return;
  try {
    history.pushState({ screen: 'play' }, '');
  } catch {
    /* no history API: Back in the app still works */
  }
  ui.patch({ screen: 'play', sheet: null });
}

export function leavePlayer(): void {
  engine.pause();
  engine.clearEnded();
  dismissToast();
  if (history.state?.screen === 'play') history.back();
  else ui.patch({ screen: 'home', sheet: null });
}

// A reload on the player keeps the history entry's "play" state while the app starts on Home; clear it, or Back from the player
// (after opening a song again) would land on that entry and stay in the player.
try {
  if (history.state?.screen === 'play') history.replaceState(null, '');
} catch {
  /* no history API */
}

window.addEventListener('popstate', () => {
  const play = history.state?.screen === 'play';
  if (!play) {
    engine.pause();
    engine.clearEnded();
    dismissToast();
  }
  ui.patch({ screen: play ? 'play' : 'home', sheet: null });
});

// ── the taps bar and the status line (DESIGN §9) ──

const NOTED_KEY = 'salsacount.countset';
let noteTimer = 0;
let removedTimer = 0;
let removedId = 0;

/** Has "Count set from N taps" been shown for this song before? Remembered on this device, so reopening a counted song does not say it again. */
export function countNoted(track: string): boolean {
  try {
    return (JSON.parse(localStorage.getItem(NOTED_KEY) ?? '[]') as string[]).includes(track);
  } catch {
    return false;
  }
}

/** Say "Count set from N taps · Replay" for ten seconds, once per song (the song is marked as told, on this device). */
export function showCountNote(track: string, taps: number): void {
  if (countNoted(track)) return;
  try {
    const seen = JSON.parse(localStorage.getItem(NOTED_KEY) ?? '[]') as string[];
    localStorage.setItem(NOTED_KEY, JSON.stringify([...seen, track].slice(-300)));
  } catch {
    /* storage refused: it may be said again on another visit */
  }
  ui.patch({ countNote: { track, taps } });
  announce(`Count set from ${taps} taps. Replay is available.`);
  window.clearTimeout(noteTimer);
  noteTimer = window.setTimeout(hideCountNote, 10_000);
}

export function hideCountNote(): void {
  window.clearTimeout(noteTimer);
  if (ui.get().countNote) ui.patch({ countNote: null });
}

/** "Tap removed · Put back" in the taps bar for six seconds. */
export function showRemovedNote(): void {
  const id = ++removedId;
  ui.patch({ removedNote: id });
  window.clearTimeout(removedTimer);
  removedTimer = window.setTimeout(() => { if (ui.get().removedNote === id) ui.patch({ removedNote: null }); }, 6000);
}

export function hideRemovedNote(): void {
  window.clearTimeout(removedTimer);
  if (ui.get().removedNote !== null) ui.patch({ removedNote: null });
}
